import React from 'react';

// Utility function for file size formatting
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Photo Preview Modal Component
const PhotoModal = ({ photo, onDownload, onDismiss, onPrev, onNext, onQuickEdit }) => {
  const [photoUrl, setPhotoUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchUrl = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        
        const response = await fetch(`${apiBase}/files/${encodeURIComponent(photo.file_key)}/download-url`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setPhotoUrl(data.presigned_url);
        }
      } catch (error) {
        console.error('Failed to fetch photo URL:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUrl();
  }, [photo.file_key]);

  // Keyboard navigation and escape to close
  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        onDismiss && onDismiss();
      } else if (e.key === 'ArrowLeft') {
        onPrev && onPrev();
      } else if (e.key === 'ArrowRight') {
        onNext && onNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onDismiss, onPrev, onNext]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onDismiss}>
      <div className="relative w-full h-full max-w-6xl max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/50 to-transparent">
          <div className="flex items-center justify-between text-white">
            <div>
              <h3 className="text-lg font-semibold truncate max-w-md">
                {photo.filename}
              </h3>
              <p className="text-sm text-gray-300">
                {formatFileSize(photo.size || 0)} • {new Date(photo.last_modified).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {onQuickEdit && (
                <button
                  onClick={() => onQuickEdit(photo)}
                  className="p-2 bg-black/50 hover:bg-black/75 rounded-lg transition-colors"
                  title="Quick Edit"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 20h9" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => onDownload(photo)}
                className="p-2 bg-black/50 hover:bg-black/75 rounded-lg transition-colors"
                title="Download"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
              <button
                onClick={onDismiss}
                className="p-2 bg-black/50 hover:bg-black/75 rounded-lg transition-colors"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Photo */}
        <div className="relative w-full h-full flex items-center justify-center">
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <p className="text-white ml-4">Loading photo...</p>
            </div>
          ) : photoUrl ? (
            <img
              src={photoUrl}
              alt={photo.filename}
              className="max-w-[90%] max-h-[80vh] object-contain rounded-lg shadow-2xl mx-auto"
            />
          ) : (
            <div className="w-64 h-64 bg-gray-800 rounded-lg flex items-center justify-center">
              <div className="text-center text-white">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-lg font-medium">Unable to load image</p>
                <p className="text-sm text-gray-400">{photo.filename}</p>
              </div>
            </div>
          )}

          {/* Navigation Arrows (always rendered for visibility) */}
          <button
            type="button"
            onClick={() => onPrev && onPrev()}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 p-3 bg-black/60 hover:bg-black/80 rounded-full text-white z-20"
            title="Previous"
            aria-label="Previous photo"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onNext && onNext()}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 p-3 bg-black/60 hover:bg-black/80 rounded-full text-white z-20"
            title="Next"
            aria-label="Next photo"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoModal;












